import React, { useState } from "react";
import "./OrderDetailsModal.css";
import GradientButton from "../../components/button/Button";
import PlainButton from "../../components/button/PlainButton";
import { GreenBox } from "../../images/Images";
import axiosInstance from "../../axiosInstance";

// Utility function to format date
const formatDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Interfaces for typing
interface Customer {
  full_name: string;
  phone_number: string;
}

interface OrderItem {
  id: string;
  name: string;
  description: string;
  quantity: number;
  price: number;
  line_total: number;
}

interface OrderDetails {
  id: string;
  createdAt: string;
  amount: number;
  status: string;
  customer: Customer;
  order_items: OrderItem[];
}

interface DetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderDetails: OrderDetails | null;
  onStatusUpdated?: () => void;
}

const OrderDetailsModal: React.FC<DetailsModalProps> = ({
  isOpen,
  onClose,
  orderDetails,
  onStatusUpdated,
}) => {
  const [complete, setComplete] = useState(
    orderDetails?.status === "delivered"
  );
  const [loading, setLoading] = useState(false);

  if (!isOpen || !orderDetails) return null;

  const handleSave = async () => {
    setLoading(true);
    try {
      if (complete && orderDetails.status !== "delivered") {
        await axiosInstance.put(`/orders/${orderDetails.id}/status`, {
          status: "delivered",
        });
        if (onStatusUpdated) onStatusUpdated();
      }
      onClose();
    } catch (error) {
      setLoading(false);
    }
  };

  const totalItems = orderDetails.order_items.reduce(
    (sum, item) => sum + item.quantity,
    0
  );

  return (
    <div className="orders-details">
      <div className="orders-details__overlay" onClick={onClose}></div>
      <div className="orders-details__content">
        <div className="orders__modal__header">
          <h2 className="orders-details__title">Order Details</h2>
          <button className="orders-details__close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="order__details__fs">
          <h4>Order {orderDetails.id}</h4>

          <div className="order__details__flex">
            <div className="order__details__fs__flexitems">
              <div>
                <p>Amount:</p>
                <b>${orderDetails.amount.toFixed(2)}</b>
              </div>
              <div>
                <p>Status:</p>
                <b>{orderDetails.status}</b>
              </div>
            </div>

            <div className="order__details__fs__flexitems">
              <div>
                <p>Order Date:</p>
                <b>{formatDate(orderDetails.createdAt)}</b>
              </div>

              <div>
                <p>Items:</p>
                <b>{totalItems}</b>
              </div>
            </div>
          </div>

          <div className="order__details__shipping">
            <p className="order__details__title">Customer Details</p>
            <div className="order__details__shipping__address">
              <div className="address__cube">
                <img src={GreenBox} alt="box" />
              </div>
              <div className="address__details">
                <b>{orderDetails.customer.full_name}</b>
                <p>{orderDetails.customer.phone_number}</p>
              </div>
            </div>

            <div className="order__details__shipping__address-orders">
              <p className="order__details__title">Ordered Products</p>
              {orderDetails.order_items.map((product, index) => (
                <div className="address__orders" key={product.id}>
                  <div className="address__orders__ls">
                    <div className="address__img">
                      <img src={GreenBox} alt={product.name} />
                    </div>
                    <div className="order__info">
                      <h4>{product.name}</h4>
                      <b>${product.price.toFixed(2)}</b>{" "}
                      <span>Line total: ${product.line_total.toFixed(2)}</span>
                    </div>
                  </div>
                  <b>x{product.quantity}</b>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="order__details__total__section">
          <div className="order__details__total">
            <p>Total</p>
            <p>${orderDetails.amount.toFixed(2)}</p>
          </div>

          <div className="order__details__total__checklist">
            <div>
              <p>Order is complete</p>
              <input
                type="checkbox"
                name="complete"
                id="complete"
                checked={complete}
                onChange={(e) => setComplete(e.target.checked)}
                disabled={orderDetails.status === "delivered"}
              />
            </div>
          </div>
        </div>

        <div className="orders-details__actions">
          <div className="orders-details__clear">
            <PlainButton text="Cancel" onClick={onClose} />
          </div>

          <div className="orders-details__apply">
            <GradientButton
              onClick={handleSave}
              text={loading ? "Saving..." : "Save Changes"}
              disabled={loading || orderDetails.status === "delivered"}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;
